import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '../lib/cn';
import {Label} from '../label';
import {ErrorMessage} from '../error-message';

export const switchVariants = cva(
  [
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
    'focus-visible:outline-none focus-visible:shadow-[var(--chekin-shadow-focus)]',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-busy:cursor-wait aria-busy:opacity-50',
    'data-[state=checked]:bg-[var(--switch-checked-bg)] data-[state=unchecked]:bg-[var(--switch-unchecked-bg)]',
  ],
  {
    variants: {
      size: {
        default: 'h-5 w-10',
        lg: 'h-5 w-10',
        sm: 'h-4 w-8',
      },
      readOnly: {
        true: 'cursor-default opacity-100',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-[var(--switch-thumb-bg)] shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
  {
    variants: {
      size: {
        default: 'h-4 w-4 data-[state=checked]:translate-x-5',
        lg: 'h-4 w-4 data-[state=checked]:translate-x-5',
        sm: 'h-3 w-3 data-[state=checked]:translate-x-4',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export interface SwitchProps
  extends
    Omit<
      React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
      'onChange' | 'value'
    >,
    VariantProps<typeof switchVariants> {
  onChange?: React.ComponentPropsWithoutRef<
    typeof SwitchPrimitives.Root
  >['onCheckedChange'];
  value?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  label?: React.ReactNode;
  error?: string;
  wrapperClassName?: string;
}

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(
  (
    {
      className,
      size,
      readOnly,
      loading,
      onChange,
      value,
      id,
      label,
      error,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const fieldId = id || generatedId;
    const switchElement = (
      <SwitchPrimitives.Root
        ref={ref}
        className={cn(switchVariants({size, readOnly}), className)}
        disabled={props.disabled && !readOnly}
        {...props}
        id={fieldId}
        onCheckedChange={readOnly ? undefined : onChange}
        checked={value}
        value={String(value)}
        aria-busy={loading}
        aria-readonly={readOnly}
      >
        <SwitchPrimitives.Thumb className={cn(switchThumbVariants({size}))} />
      </SwitchPrimitives.Root>
    );

    if (!label && !error) {
      return switchElement;
    }

    return (
      <>
        <div className={cn('flex items-center gap-x-3 gap-y-1.5', wrapperClassName)}>
          {switchElement}
          {label && (
            <Label htmlFor={fieldId} className="cursor-pointer text-base font-medium">
              {label}
            </Label>
          )}
        </div>
        {error && <ErrorMessage disabled={props.disabled}>{error}</ErrorMessage>}
      </>
    );
  },
);

Switch.displayName = SwitchPrimitives.Root.displayName;
